<script>
// Seletor de cliente no topo da sidebar de cliente. Lê o cliente atual e as
// listas (histórico + geral) do store `user`, e troca o cliente via
// navigation/enterClient (que reusa user/addSearchClient). O roteamento pós-troca
// fica a cargo do componente pai (evento 'picked').
import navSurface from '@modules/navegacao-v2/mixins/nav-surface'
import { formatBrokerCode } from '@utils/cliente-codigo/cliente-codigo'
import { filterClients } from './client-switcher-helpers'

const SEARCH_ICON_SIZE = 14

export default {
  name: 'ClientSwitcher',
  mixins: [navSurface],
  props: {
    collapsed: { type: Boolean, default: false },
  },
  data() {
    return { open: false, query: '', SEARCH_ICON_SIZE }
  },
  computed: {
    client() {
      return this.$currentClient() || {}
    },
    recents() {
      return this.$store.getters['user/getSearchedClients'] || []
    },
    allClients() {
      return this.$store.getters['user/allClients'] || []
    },
    searchPool() {
      return this.allClients.length ? this.allClients : this.recents
    },
    results() {
      return filterClients(this.searchPool, this.query)
    },
    list() {
      return this.query ? this.results : this.recents
    },
    clientSubtitle() {
      return formatBrokerCode(this.client.codigo_corretora)
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.onOutside)
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onOutside)
  },
  methods: {
    initials(name) {
      return (name || '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase()
    },
    isLead(c) {
      return !!(c && c.cliente_prospect)
    },
    toggle() {
      this.open = !this.open
    },
    onOutside(event) {
      if (this.$el && !this.$el.contains(event.target)) this.open = false
    },
    pick(client) {
      this.$store.dispatch('navigation/enterClient', client)
      this.open = false
      this.query = ''
      this.$emit('picked', client)
    },
    openAll() {
      this.open = false
      this.$emit('open-all')
    },
  },
}
</script>

<template>
  <div class="client-switcher" :class="{ 'client-switcher--collapsed': collapsed }">
    <button class="client-switcher__current" type="button" :title="collapsed ? (client.name || 'Selecionar cliente') : null" @click="toggle">
      <span class="client-switcher__avatar">{{ initials(client.name) }}</span>
      <span v-if="!collapsed" class="client-switcher__meta">
        <span class="client-switcher__name-row">
          <span class="client-switcher__name">{{ client.name || 'Selecionar cliente' }}</span>
          <span v-if="isLead(client)" class="client-switcher__lead">Lead</span>
        </span>
        <span v-if="clientSubtitle" class="client-switcher__sub">{{ clientSubtitle }}</span>
      </span>
      <icon-svg v-if="!collapsed" name="mdi-chevron-down" :size="16" :color="navFg" />
    </button>

    <div v-if="open" class="client-switcher__dropdown">
      <div class="client-switcher__search">
        <icon-svg name="status-search" :size="SEARCH_ICON_SIZE" :color="navFg" />
        <input
          v-model="query"
          class="client-switcher__input"
          type="text"
          placeholder="Buscar cliente ou código…"
        />
      </div>

      <div v-if="!query && recents.length" class="client-switcher__section">Recentes</div>

      <div class="client-switcher__list">
        <button
          v-for="c in list"
          :key="c.id"
          class="client-switcher__option"
          :class="{ 'client-switcher__option--current': c.id === client.id }"
          type="button"
          @click="pick(c)"
        >
          <span class="client-switcher__avatar client-switcher__avatar--sm">{{ initials(c.name) }}</span>
          <span class="client-switcher__opt-name">{{ c.name }}</span>
          <span v-if="isLead(c)" class="client-switcher__lead">Lead</span>
          <icon-svg
            v-if="c.id === client.id"
            name="check-circle"
            :size="16"
            color="primary"
          />
        </button>

        <div v-if="query && !list.length" class="client-switcher__empty">
          Nenhum cliente encontrado.
        </div>
      </div>

      <button class="client-switcher__all" type="button" @click="openAll">
        Ver todos os clientes
      </button>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.client-switcher
  position: relative
  padding: 18px 16px 16px
  border-bottom: 1px solid rgba(var(--nav-fg-rgb, 255, 255, 255), 0.08)

  // Rail recolhido (84px): só o avatar centrado. Sem o tratamento abaixo, o
  // padding lateral + o avatar de 42px (flex-shrink:0) estouram a largura e
  // quebram o layout. O dropdown vira um painel que transborda para a direita.
  &--collapsed
    padding: 14px 0
    display: flex
    justify-content: center

  &--collapsed &__current
    width: 48px
    padding: 0
    justify-content: center
    background: transparent
    box-shadow: none

    &:hover
      background: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.06)

  &--collapsed &__avatar
    width: 40px
    height: 40px
    font-size: 14px

  &--collapsed &__dropdown
    left: 12px
    right: auto
    top: calc(100% - 6px)
    width: 280px

  &__current
    display: flex
    align-items: center
    gap: 12px
    width: 100%
    box-sizing: border-box
    padding: 10px 12px
    border: 0
    border-radius: 12px
    background: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.04)
    box-shadow: inset 0 0 0 1px rgba(var(--nav-fg-rgb, 255, 255, 255), 0.1)
    cursor: pointer
    text-align: left

    &:hover
      background: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.08)

  &__avatar
    flex-shrink: 0
    width: 42px
    height: 42px
    border-radius: 12px
    background: var(--primary)
    color: var(--text-on-primary, #fff)
    font-weight: 700
    font-size: 15px
    display: flex
    align-items: center
    justify-content: center

    &--sm
      width: 30px
      height: 30px
      font-size: 12px
      border-radius: 9px

  &__meta
    flex: 1
    min-width: 0
    display: flex
    flex-direction: column

  &__name-row
    display: flex
    align-items: center
    gap: 8px
    min-width: 0

  &__name
    font-size: 15px
    font-weight: 700
    color: var(--nav-fg, #fff)
    line-height: 1.2
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis

  &__lead
    flex-shrink: 0
    font-size: 9px
    font-weight: 700
    letter-spacing: 0.06em
    text-transform: uppercase
    color: var(--nav-lead-fg, #ffd166)
    background: rgba(255, 209, 102, 0.16)
    padding: 2px 7px
    border-radius: 999px

  &__sub
    font-size: 10.5px
    color: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.42)
    margin-top: 2px
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis

  &__dropdown
    position: absolute
    left: 12px
    right: 12px
    top: calc(100% - 2px)
    z-index: 40
    padding: 8px
    border-radius: 14px
    background: var(--nav-panel-bg, #15151a)
    border: 1px solid rgba(var(--nav-fg-rgb, 255, 255, 255), 0.1)
    box-shadow: 0 18px 48px rgba(0, 0, 0, 0.5)

  &__search
    display: flex
    align-items: center
    gap: 8px
    padding: 0 10px
    margin-bottom: 6px
    border-radius: 9px
    border: 1px solid rgba(var(--nav-fg-rgb, 255, 255, 255), 0.12)
    background: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.05)

  &__input
    flex: 1
    height: 38px
    border: 0
    background: transparent
    color: var(--nav-fg, #fff)
    font-size: 13px
    outline: none

  &__section
    padding: 6px 10px 4px
    font-size: 9.5px
    font-weight: 700
    letter-spacing: 0.12em
    text-transform: uppercase
    color: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.35)

  &__list
    max-height: 268px
    overflow-y: auto

  &__option
    display: flex
    align-items: center
    gap: 10px
    width: 100%
    padding: 8px 10px
    border: 0
    border-radius: 9px
    background: transparent
    color: var(--nav-fg, #fff)
    cursor: pointer
    text-align: left

    &:hover
      background: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.05)

    &--current
      background: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.06)

  &__opt-name
    flex: 1
    min-width: 0
    font-size: 13px
    font-weight: 600
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis

  &__empty
    padding: 14px 10px
    font-size: 12.5px
    color: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.5)

  &__all
    width: 100%
    margin-top: 6px
    padding: 10px
    border: 0
    border-top: 1px solid rgba(var(--nav-fg-rgb, 255, 255, 255), 0.08)
    background: transparent
    color: var(--primary)
    font-size: 12.5px
    font-weight: 700
    cursor: pointer
</style>
